import React, { startTransition, useEffect, useMemo, useRef, useState } from "react";
import { Canvas, useFrame } from "@react-three/fiber";
import * as THREE from "three";

import { APP_ACTIONS } from "../../state/actionTypes";
import { fetchJson } from "../../utils/api";
import { getMeetJozLogicalLayer } from "../../world-model/meetJoz";
import {
  WORLDX_SECTORS,
  worldxLandmarkFromView,
} from "../../world-model/portalSpatialModel";
import {
  MAXX_PLAYBACK_STATE_EVENT,
  readMaxxPlaybackStatus,
  requestMaxxPlayback,
} from "../maxx/playbackController";
import { recordLivePortalAwareness } from "../../world-model/liveAppPerception";

const PERCEPTION_CACHE_LIMIT = 64;
const PERCEPTION_SETTLE_MS = 300;
const PERCEPTION_MIN_INTERVAL_MS = 800;
const PERCEPTION_ROLL_DURATION_MS = 520;
const ROOT_LOOK_AT_SENTENCES = Object.freeze({
  exocortex: "On the Moon,",
  aura_ascend: "On the Moon, Monitoring the Aura Ascend, link up with Joz.",
  ascend: "On the Moon, Monitoring Ascend",
  aura: "On the Moon, Monitoring Aura",
  gold_pill: "On the Moon, Monitoring the Gold Pill",
  sector_left: "On the Moon, Monitoring Left Sector",
  sector_right: "On the Moon, Monitoring Right Sector",
  sector_lower: "On the Moon, Monitoring Lower Sector",
  moon_only: "On the Moon, Monitoring the surrounding space.",
});
const MAXX_LOOK_AT_SENTENCES = Object.freeze({
  neural_activity: "inside the Exocortex. Pause Neural Flow or View in Your World.",
  paused: "inside the Exocortex. Resume Neural Flow or View in Your World.",
  neurodesign: "inside the Exocortex. Neurodesign is visible. Resume Neural Flow or View in Your World.",
  inside: "inside the Exocortex.",
});
const MEET_JOZ_LOOK_AT_SENTENCES = Object.freeze({
  flex: "at Flex, begin your Ascension.",
  ascend: "at Ascend, next on Mogg.",
  mogg: "at Mogg. Show Skills.",
  skills: "at Skills. View in Your World.",
});
const ROOT_PORTAL_ACTION_SUFFIX = "enter the Exocortex or Meet Joz.";
const ROOT_CONTACT_ACTION_SUFFIX = "link up with Joz.";
const NON_FOCUS_SEMANTIC_IDS = new Set([
  "root_fluid_co_creation",
  "meet_joz_transition_fluid",
]);

const LIVE_PERCEPTION_CUBE_COLORS = [
  "#6d7dff",
  "#247bd5",
  "#a891ff",
  "#17245f",
  "#4de0c5",
  "#8deaff",
];

function shortestAngleDelta(next, previous) {
  return Math.atan2(Math.sin(next - previous), Math.cos(next - previous));
}

function LivePerceptionCubeModel({ initialCamera = {}, poseRef, targetScale = 1, initialScale = targetScale }) {
  const initialPose = poseRef?.current || { x: 0.48, y: -0.62, z: 0 };
  const meshRef = useRef(null);
  const cubeScaleTargetRef = useRef(targetScale);
  const cubeScaleStartRef = useRef(null);
  const cubeScaleFromRef = useRef(initialScale);
  const interactingRef = useRef(false);
  const reducedMotionRef = useRef(false);
  const lastCameraYawRef = useRef(THREE.MathUtils.degToRad(Number(initialCamera.yawDegrees) || 0));
  const continuousCameraYawRef = useRef(lastCameraYawRef.current);
  const interactionStartCameraYawRef = useRef(lastCameraYawRef.current);
  const interactionStartCameraPitchRef = useRef(THREE.MathUtils.degToRad(Number(initialCamera.pitchDegrees) || 0));
  const interactionStartMeshYawRef = useRef(initialPose.y);
  const interactionStartMeshPitchRef = useRef(initialPose.x);
  const targetYawRef = useRef(initialPose.y);
  const targetPitchRef = useRef(initialPose.x);

  useEffect(() => {
    cubeScaleTargetRef.current = targetScale;
    cubeScaleFromRef.current = meshRef.current
      ? meshRef.current.scale.x / 0.92
      : initialScale;
    cubeScaleStartRef.current = null;
  }, [initialScale, targetScale]);

  useEffect(() => {
    if (typeof window === "undefined") return undefined;
    const motionPreference = window.matchMedia?.("(prefers-reduced-motion: reduce)");
    const syncMotionPreference = () => {
      reducedMotionRef.current = motionPreference?.matches === true;
    };
    syncMotionPreference();
    motionPreference?.addEventListener?.("change", syncMotionPreference);
    return () => motionPreference?.removeEventListener?.("change", syncMotionPreference);
  }, []);

  useEffect(() => {
    if (typeof window === "undefined") return undefined;
    const handleCanvasInteraction = (event) => {
      const view = event.detail?.cameraState?.view || {};
      const yawDegrees = Number(view.yaw_degrees);
      const pitchDegrees = Number(view.pitch_degrees);
      if (!Number.isFinite(yawDegrees) || !Number.isFinite(pitchDegrees)) return;

      const nextCameraYaw = THREE.MathUtils.degToRad(yawDegrees);
      const nextCameraPitch = THREE.MathUtils.degToRad(pitchDegrees);
      const isActive = event.detail?.active === true;
      const wasActive = interactingRef.current;

      if (isActive && !wasActive) {
        continuousCameraYawRef.current = nextCameraYaw;
        interactionStartCameraYawRef.current = nextCameraYaw;
        interactionStartCameraPitchRef.current = nextCameraPitch;
        interactionStartMeshYawRef.current = meshRef.current?.rotation.y ?? targetYawRef.current;
        interactionStartMeshPitchRef.current = meshRef.current?.rotation.x ?? targetPitchRef.current;
      } else if (isActive) {
        continuousCameraYawRef.current += shortestAngleDelta(
          nextCameraYaw,
          lastCameraYawRef.current
        );
      }

      lastCameraYawRef.current = nextCameraYaw;
      if (isActive || wasActive) {
        targetYawRef.current = interactionStartMeshYawRef.current + (
          continuousCameraYawRef.current - interactionStartCameraYawRef.current
        );
        targetPitchRef.current = THREE.MathUtils.clamp(
          interactionStartMeshPitchRef.current - (
            nextCameraPitch - interactionStartCameraPitchRef.current
          ),
          -Math.PI * 0.42,
          Math.PI * 0.42
        );
      }

      interactingRef.current = isActive;
      if (!isActive && wasActive && meshRef.current) {
        meshRef.current.rotation.y = targetYawRef.current;
        meshRef.current.rotation.x = targetPitchRef.current;
      }
    };

    window.addEventListener("world-camera-interaction", handleCanvasInteraction);
    return () => window.removeEventListener("world-camera-interaction", handleCanvasInteraction);
  }, []);

  useFrame((state, delta) => {
    const mesh = meshRef.current;
    if (!mesh) return;
    if (cubeScaleStartRef.current === null) {
      cubeScaleStartRef.current = state.clock.elapsedTime;
    }
    const scaleProgress = THREE.MathUtils.clamp(
      (state.clock.elapsedTime - cubeScaleStartRef.current) / 0.52,
      0,
      1
    );
    const easedScaleProgress = scaleProgress * scaleProgress * (3 - 2 * scaleProgress);
    const nextScale = THREE.MathUtils.lerp(
      cubeScaleFromRef.current,
      cubeScaleTargetRef.current,
      easedScaleProgress
    );
    mesh.scale.setScalar(0.92 * nextScale);
    if (interactingRef.current) {
      mesh.rotation.y = THREE.MathUtils.damp(mesh.rotation.y, targetYawRef.current, 22, delta);
      mesh.rotation.x = THREE.MathUtils.damp(mesh.rotation.x, targetPitchRef.current, 22, delta);
      mesh.rotation.z = THREE.MathUtils.damp(mesh.rotation.z, 0, 18, delta);
    } else {
      if (!reducedMotionRef.current) mesh.rotation.y += delta * 0.72;
      mesh.rotation.x = THREE.MathUtils.damp(mesh.rotation.x, targetPitchRef.current, 3, delta);
      mesh.rotation.z = THREE.MathUtils.damp(mesh.rotation.z, Math.sin(state.clock.elapsedTime * 0.8) * 0.035, 2, delta);
    }
    if (poseRef) {
      poseRef.current = {
        x: mesh.rotation.x,
        y: mesh.rotation.y,
        z: mesh.rotation.z,
      };
    }
  });

  return (
    <mesh
      ref={meshRef}
      rotation={[initialPose.x, initialPose.y, initialPose.z]}
      scale={0.92 * initialScale}
    >
      <boxGeometry args={[1, 1, 1]} />
      {LIVE_PERCEPTION_CUBE_COLORS.map((color, index) => (
        <meshStandardMaterial
          key={color}
          attach={`material-${index}`}
          color={color}
          emissive={color}
          emissiveIntensity={0.12}
          metalness={0.3}
          roughness={0.24}
        />
      ))}
    </mesh>
  );
}

function LivePerceptionCubeMesh({ camera = {}, poseRef, scale = 1, initialScale = scale }) {
  return (
    <span className="live-perception__cube-mesh" aria-hidden="true">
      <Canvas
        orthographic
        eventSource={typeof document !== "undefined" ? document.body : undefined}
        camera={{
          left: -1.05,
          right: 1.05,
          top: 1.05,
          bottom: -1.05,
          near: 0.1,
          far: 20,
          position: [0, 0, 5]
        }}
        dpr={[2, 4]}
        gl={{ alpha: true, antialias: true, powerPreference: "high-performance" }}
        style={{ width: "100%", height: "100%", pointerEvents: "none" }}
      >
        <ambientLight intensity={1.8} />
        <directionalLight position={[2.5, 3.5, 4]} intensity={3.2} color="#ffffff" />
        <directionalLight position={[-3, -2, 2]} intensity={1.8} color="#6e8dff" />
        <pointLight position={[0, 1.5, 2.5]} intensity={2.2} color="#79ffe3" />
        <LivePerceptionCubeModel
          initialCamera={camera}
          poseRef={poseRef}
          targetScale={scale}
          initialScale={initialScale}
        />
      </Canvas>
    </span>
  );
}

function clean(value, maxLength = 240) {
  return String(value || "").trim().slice(0, maxLength);
}

function finiteNumber(value) {
  const number = Number(value);
  return Number.isFinite(number) ? number : null;
}

function normalizedPortal(value) {
  const portal = clean(value, 48).toLowerCase().replace(/_/g, "-");
  if (["maxx", "neo-maxx", "neomaxx", "the-vibe-energy"].includes(portal)) return "maxx";
  if (["meet-joz", "meetjoz"].includes(portal)) return "meet-joz";
  return portal || "root";
}

function worldxSectorIndex(yawDegrees, pitchDegrees = 0) {
  if (!Number.isFinite(yawDegrees)) return null;
  return worldxLandmarkFromView({ headingDegrees: yawDegrees, pitchDegrees })?.sectorIndex ?? null;
}

function compactSemanticObject(object = {}) {
  const spatial = object.spatial || {};
  const bearingDegrees = finiteNumber(spatial.bearing_degrees);
  const elevationDegrees = finiteNumber(spatial.elevation_degrees);
  const inFront = typeof spatial.in_front === "boolean"
    ? spatial.in_front
    : Number.isFinite(bearingDegrees) && Number.isFinite(elevationDegrees)
      ? Math.abs(bearingDegrees) <= 45 && Math.abs(elevationDegrees) <= 45
      : null;
  return {
    id: clean(object.id, 96),
    role: clean(object.role, 96) || null,
    meaning: clean(object.meaning, 420) || null,
    interactive: object.interactive === true,
    action: clean(object.action, 96) || null,
    target: clean(object.target, 180) || null,
    spatial: {
      horizontal: clean(spatial.horizontal, 48) || null,
      vertical: clean(spatial.vertical, 48) || null,
      bearingDegrees,
      elevationDegrees,
      distance: finiteNumber(spatial.distance),
      inFront,
    },
  };
}

function deriveLookAtEvidence(rawEvidence, semanticObjects = []) {
  if (["center-ray", "portal-spatial-model"].includes(rawEvidence?.method) && rawEvidence.primaryObjectId) {
    return rawEvidence;
  }

  const candidates = semanticObjects
    .map((object) => {
      const spatial = object.spatial || {};
      const bearing = Number(spatial.bearingDegrees);
      const elevation = Number(spatial.elevationDegrees);
      return {
        id: object.id,
        bearing,
        elevation,
        distance: Number(spatial.distance),
      };
    })
    .filter((candidate) => Number.isFinite(candidate.bearing) && Number.isFinite(candidate.elevation))
    .map((candidate) => ({
      ...candidate,
      angularScore: Math.hypot(candidate.bearing, candidate.elevation),
    }))
    .filter((candidate) =>
      !NON_FOCUS_SEMANTIC_IDS.has(String(candidate.id || "").toLowerCase()) &&
      candidate.angularScore <= 90
    )
    .sort((left, right) => {
      if (left.angularScore !== right.angularScore) return left.angularScore - right.angularScore;
      return (left.distance || Infinity) - (right.distance || Infinity);
    })
    .slice(0, 8);

  if (!candidates.length) return rawEvidence || null;
  return {
    method: "center-ray",
    primaryObjectId: candidates[0].id,
    candidates: candidates.map((candidate) => ({
      id: candidate.id,
      hitDistance: Number.isFinite(candidate.distance) ? candidate.distance : null,
      hitObjectId: null,
    })),
    confidence: rawEvidence?.confidence === "high" ? "high" : "medium",
  };
}

export function buildLivePerceptionRequest({
  currentPortal = "root",
  currentMesh = null,
  currentStage = null,
  currentPhase = null,
  sceneSnapshot = null,
  showWorldx = false,
  showMogg = false,
  isWorkStepInteractive = false,
  isMobile = false,
} = {}) {
  const portal = normalizedPortal(currentPortal);
  const observedPortal = normalizedPortal(sceneSnapshot?.sceneState?.activePortal);
  const observationMatchesPortal = observedPortal === portal;
  const sceneState = observationMatchesPortal ? sceneSnapshot?.sceneState || {} : {};
  const view = observationMatchesPortal ? sceneSnapshot?.cameraState?.view || {} : {};
  const yawDegrees = finiteNumber(view.yaw_degrees);
  const pitchDegrees = finiteNumber(view.pitch_degrees);
  const headingDegrees = Number.isFinite(yawDegrees)
    ? ((yawDegrees % 360) + 360) % 360
    : null;
  const worldxCenterEvidence = observationMatchesPortal
    ? sceneSnapshot?.worldxCenterEvidence || null
    : null;
  const centerSector = finiteNumber(worldxCenterEvidence?.sectorIndex);
  const worldxAvailable = !isMobile && showWorldx === true;
  const focusedEntityId = clean(sceneState.focusedEntityId || currentMesh, 96) || null;
  const stage = clean(sceneState.activeStage || currentStage, 96) || null;
  const semanticObjects = Array.isArray(sceneState.visibleSemanticObjects)
    ? sceneState.visibleSemanticObjects.slice(0, 48).map(compactSemanticObject).filter((object) => object.id)
    : [];
  const semanticActions = Array.isArray(sceneState.availableSemanticActions)
    ? sceneState.availableSemanticActions.slice(0, 24).map((action) => ({
        id: clean(action?.id || action?.action, 96),
        action: clean(action?.action, 96) || null,
        target: clean(action?.target, 160) || null,
        label: clean(action?.label, 120) || null,
      })).filter((action) => action.id)
    : [];
  const rawLookAtEvidence = sceneSnapshot?.lookAtEvidence || null;
  const layer = portal === "meet-joz"
    ? getMeetJozLogicalLayer({
        mesh: focusedEntityId,
        stage,
        showModel2: worldxAvailable,
        showModel4: showMogg,
        isWorkStepInteractive,
      })
    : null;

  return {
    experience: {
      portal,
      focusedEntityId,
      stage,
      phase: clean(currentPhase, 96) || null,
      layer,
      isMobile: isMobile === true,
      worldxVisible: worldxAvailable,
      moggVisible: showMogg === true,
      workDetailVisible: isWorkStepInteractive === true,
    },
    camera: {
      yawDegrees,
      headingDegrees,
      pitchDegrees,
      position: Array.isArray(sceneSnapshot?.cameraState?.position)
        ? sceneSnapshot.cameraState.position.slice(0, 3).map(finiteNumber)
        : null,
      horizontalSector: clean(view.horizontal_sector, 48) || null,
      verticalBand: clean(view.vertical_band, 48) || null,
      previousHorizontalSector: clean(view.previous_horizontal_sector, 48) || null,
      rotationDirection: clean(view.rotation_direction, 48) || null,
      rotationDeltaDegrees: finiteNumber(view.rotation_delta_degrees),
      yawVelocity: finiteNumber(view.yaw_velocity),
      pitchVelocity: finiteNumber(view.pitch_velocity),
      stability: finiteNumber(view.stability),
      lastRotation: view.last_rotation && typeof view.last_rotation === "object"
        ? {
            fromSector: clean(view.last_rotation.from_sector, 48) || null,
            towardSector: clean(view.last_rotation.toward_sector, 48) || null,
            direction: clean(view.last_rotation.direction, 48) || null,
            degrees: finiteNumber(view.last_rotation.degrees),
          }
        : null,
      relativeWorldxSector: worldxAvailable
        ? Number.isInteger(centerSector) && centerSector >= 0 && centerSector < WORLDX_SECTORS.length
          ? centerSector
          : worldxSectorIndex(yawDegrees, pitchDegrees)
        : null,
      centerEvidence: worldxAvailable && worldxCenterEvidence
        ? {
            method: clean(worldxCenterEvidence.method, 48) || null,
            headingDegrees: finiteNumber(worldxCenterEvidence.headingDegrees),
            pitchDegrees: finiteNumber(worldxCenterEvidence.pitchDegrees),
            sectorIndex: centerSector,
            label: clean(worldxCenterEvidence.label, 120) || null,
            calibratedHeadingDegrees: finiteNumber(worldxCenterEvidence.calibratedHeadingDegrees),
            calibratedElevationDegrees: finiteNumber(worldxCenterEvidence.calibratedElevationDegrees),
            viewportCenterDistance: finiteNumber(worldxCenterEvidence.viewportCenterDistance),
            modelAsset: clean(worldxCenterEvidence.modelAsset, 120) || null,
            canvasCoordinates: worldxCenterEvidence.canvasCoordinates && typeof worldxCenterEvidence.canvasCoordinates === "object"
              ? {
                  x: finiteNumber(worldxCenterEvidence.canvasCoordinates.x),
                  y: finiteNumber(worldxCenterEvidence.canvasCoordinates.y),
                }
              : null,
          }
        : null,
    },
    lookAtEvidence: deriveLookAtEvidence(
      rawLookAtEvidence && typeof rawLookAtEvidence === "object"
        ? {
          method: clean(rawLookAtEvidence.method, 32) || null,
          primaryObjectId: clean(rawLookAtEvidence.primaryObjectId, 96) || null,
          candidateObjectIds: Array.isArray(rawLookAtEvidence.candidates)
            ? rawLookAtEvidence.candidates.map((candidate) => clean(candidate?.id, 96)).filter(Boolean).slice(0, 8)
            : [],
          candidates: Array.isArray(rawLookAtEvidence.candidates)
            ? rawLookAtEvidence.candidates.slice(0, 8).map((candidate) => ({
              id: clean(candidate?.id, 96),
              hitDistance: finiteNumber(candidate?.hitDistance),
              hitObjectId: clean(candidate?.hitObjectId, 96) || null,
              asset: clean(candidate?.asset, 120) || null,
              bearingDegrees: finiteNumber(candidate?.bearingDegrees),
              elevationDegrees: finiteNumber(candidate?.elevationDegrees),
              angularDistanceDegrees: finiteNumber(candidate?.angularDistanceDegrees),
            })).filter((candidate) => candidate.id)
            : [],
          confidence: clean(rawLookAtEvidence.confidence, 24) || "none",
          modelVersion: clean(rawLookAtEvidence.modelVersion, 64) || null,
          sectorId: clean(rawLookAtEvidence.sectorId, 64) || null,
          elevationBand: clean(rawLookAtEvidence.elevationBand, 32) || null,
        }
        : null,
      semanticObjects
    ),
    visibleSemanticObjects: semanticObjects,
    availableActions: semanticActions,
    device: { mobile: isMobile === true },
    interaction: {
      cameraActive: sceneSnapshot?.runtimeStatus?.interacting === true,
    },
    observedAt: clean(sceneSnapshot?.observedAt, 64) || null,
  };
}

export function livePerceptionSignature(request = {}) {
  const yaw = finiteNumber(request?.camera?.yawDegrees);
  const pitch = finiteNumber(request?.camera?.pitchDegrees);
  return JSON.stringify({
    portal: request?.experience?.portal || "root",
    focus: request?.experience?.focusedEntityId || null,
    stage: request?.experience?.stage || null,
    phase: request?.experience?.phase || null,
    layer: request?.experience?.layer || null,
    worldx: request?.experience?.worldxVisible === true,
    mogg: request?.experience?.moggVisible === true,
    workDetail: request?.experience?.workDetailVisible === true,
    yaw10: Number.isFinite(yaw) ? Math.round(yaw / 10) : null,
    pitch10: Number.isFinite(pitch) ? Math.round(pitch / 10) : null,
    semanticObjects: (request?.visibleSemanticObjects || []).map((object) => object.id),
    actions: (request?.availableActions || []).map((action) => action.id),
    lookAt: request?.lookAtEvidence?.primaryObjectId || null,
    lookAtCandidates: (request?.lookAtEvidence?.candidateObjectIds || []).slice(0, 8),
  });
}

export function readVerifiedAiPerception(payload = {}) {
  const source = clean(payload?.source, 32).toLowerCase();
  if (!(source === "ai" || source === "deterministic") || payload?.verified !== true) return null;
  const perception = payload.perception || {};
  const orientation = clean(perception.orientation, 100);
  const lookingAt = clean(perception.lookingAt, 320);
  const canDo = clean(perception.canDo, 260);
  if (!orientation || !lookingAt || !canDo) return null;
  return {
    source,
    orientation,
    lookingAt,
    canDo,
    situation: clean(perception.situation || lookingAt, 600),
    focusObjectId: clean(perception.focusObjectId, 96).toLowerCase() || null,
    salientObjectIds: Array.isArray(perception.salientObjectIds)
      ? perception.salientObjectIds.slice(0, 12).map((value) => clean(value, 96).toLowerCase()).filter(Boolean)
      : [],
    changes: Array.isArray(perception.changes)
      ? perception.changes.slice(0, 8).map((value) => clean(value, 240)).filter(Boolean)
      : [],
    uncertainties: Array.isArray(perception.uncertainties)
      ? perception.uncertainties.slice(0, 8).map((value) => clean(value, 240)).filter(Boolean)
      : [],
    evidenceIds: Array.isArray(payload.evidenceIds)
      ? payload.evidenceIds.slice(0, 16).map((value) => clean(value, 96).toLowerCase()).filter(Boolean)
      : [],
    confidence: clean(perception.confidence, 24) || "unknown",
  };
}

function humanize(value = "") {
  return String(value || "")
    .replace(/[_-]+/g, " ")
    .replace(/\b\w/g, (character) => character.toUpperCase())
    .trim();
}

function conciseLabel(value = "") {
  return String(value || "")
    .trim()
    .split(/[.;:!?]/, 1)[0]
    .trim();
}

function formatYouAreContinuation(value = "") {
  const text = String(value || "").trim();
  if (!text) return text;
  return text
    .replace(/^On the Moon\b/, "on the Moon")
    .replace(/^on the moon\b/i, "on the Moon")
    .replace(/^(on the Moon, )Monitoring\b/, "$1monitoring");
}

function renderRollingFocusText(value = "") {
  return String(value || "").split(/(\s+)/).map((part, index) => {
    if (/^\s+$/.test(part)) {
      return <React.Fragment key={`focus-space-${index}`}>{part}</React.Fragment>;
    }

    return (
      <span
        className="live-perception__focus-word"
        data-focus-word={part}
        key={`focus-word-${index}-${part}`}
        style={{ "--live-perception-word-index": index }}
      >
        {Array.from(part).map((letter, letterIndex) => (
          <span
            className="live-perception__focus-letter"
            key={`focus-letter-${index}-${letterIndex}-${letter}`}
            style={{ "--live-perception-letter-index": letterIndex }}
          >
            {letter}
          </span>
        ))}
      </span>
    );
  });
}

const SPATIAL_AI_SEPARATOR = "   ";

function AnimatedMetricDigit({ value = "0", digitIndex = 0 }) {
  const nextDigit = Number(value);
  const [displayedDigit, setDisplayedDigit] = useState(nextDigit);

  useEffect(() => {
    if (displayedDigit === nextDigit) return;
    setDisplayedDigit(nextDigit);
  }, [displayedDigit, nextDigit]);

  return (
    <span className="live-perception__metric-digit-window">
      <span className="live-perception__metric-digit-sizer" aria-hidden="true">
        {value}
      </span>
      <span
        className="live-perception__metric-digit-reel"
        style={{
          transform: `translateY(-${displayedDigit * 1.2}em)`,
          "--live-perception-digit-index": digitIndex,
        }}
      >
        {Array.from({ length: 10 }, (_, digit) => (
          <span className="live-perception__metric-digit-face" key={digit}>
            {digit}
          </span>
        ))}
      </span>
    </span>
  );
}

function renderMetricLine(line = "", lineIndex = 0) {
  let digitPosition = 0;
  return String(line).split(/(\d)/).map((part, partIndex) => {
    if (!/^\d$/.test(part)) {
      return (
        <span className="live-perception__metric-text" key={`metric-text-${lineIndex}-${partIndex}`}>
          {part}
        </span>
      );
    }

    const digitIndex = digitPosition;
    digitPosition += 1;
    return <AnimatedMetricDigit value={part} digitIndex={digitIndex} key={`metric-digit-${lineIndex}-${partIndex}`} />;
  });
}

function normalizeTelemetryObjectId(value) {
  return String(value || "").trim().toLowerCase();
}

const GEOMETRY_TARGET_GROUPS = Object.freeze([
  Object.freeze(["meet_joz_mogg", "meet_joz_skills"]),
  Object.freeze(["meet_joz_ascend", "meet_joz_ascend_particles", "meet_joz_semantic_city"]),
]);

// Verified source-geometry totals keep the HUD informative while the renderer
// is still loading or between animation targets. Live scene counts take
// precedence as soon as the renderer publishes them.
const VERIFIED_GEOMETRY_TOTALS = Object.freeze({
  root_brain: Object.freeze({ vertices: 696383, faces: 665309 }),
  // The Moon Exocortex is the bx.glb brain plus the enter.glb shell. Both
  // tracked targets therefore report the same combined geometry total.
  root_enter: Object.freeze({ vertices: 696383, faces: 665309 }),
  root_ascend: Object.freeze({ vertices: 2759, faces: 2726 }),
  root_aura: Object.freeze({ vertices: 2759, faces: 2726 }),
  // Moon Ascend and Aura views keep the Exocortex scene in context.
  root_ascend_aura: Object.freeze({ vertices: 701901, faces: 670761 }),
  root_gold_pill: Object.freeze({ vertices: 104571, faces: 207921 }),
  // neoMAXX shows both neuron assets together while the sequence is active.
  maxx_neurons: Object.freeze({ vertices: 379068, faces: 505373 }),
  maxx_receptors: Object.freeze({ vertices: 379068, faces: 505373 }),
  // Pausing reveals neurodesign while n2x and n3 remain visible.
  maxx_neurons_paused: Object.freeze({ vertices: 509383, faces: 736578 }),
  maxx_neurodesign: Object.freeze({ vertices: 509383, faces: 736578 }),
  meet_joz_flex: Object.freeze({ vertices: 3680, faces: 2146 }),
  meet_joz_flex_worldx: Object.freeze({ vertices: 285223, faces: 467445 }),
  meet_joz_ascend: Object.freeze({ vertices: 104832, faces: 208158 }),
  meet_joz_ascend_worldx: Object.freeze({ vertices: 386375, faces: 673457 }),
  meet_joz_ascend_particles: Object.freeze({ vertices: 386375, faces: 673457 }),
  meet_joz_semantic_city: Object.freeze({ vertices: 386375, faces: 673457 }),
  meet_joz_mogg: Object.freeze({ vertices: 479, faces: 477 }),
  meet_joz_mogg_worldx: Object.freeze({ vertices: 282022, faces: 465776 }),
  meet_joz_skills: Object.freeze({ vertices: 986986, faces: 1083287 }),
  meet_joz_skills_panel: Object.freeze({ vertices: 68689, faces: 65758 }),
});

function geometryTargetsRepresentSameStage(currentTargetId, statsTargetId) {
  if (!currentTargetId || !statsTargetId) return false;
  return GEOMETRY_TARGET_GROUPS.some((group) => (
    group.includes(currentTargetId) && group.includes(statsTargetId)
  ));
}

function geometryStatsMatchLookAt(viewStats = null, lookAtEvidence = null) {
  const currentTargetId = normalizeTelemetryObjectId(lookAtEvidence?.primaryObjectId);
  const statsTargetId = normalizeTelemetryObjectId(viewStats?.trackedObjectId);
  const hasLiveGeometry = Number(viewStats?.vertices) > 0 || Number(viewStats?.faces) > 0;
  if (!hasLiveGeometry) return false;
  if (!currentTargetId) return !statsTargetId;
  return statsTargetId === currentTargetId
    || geometryTargetsRepresentSameStage(currentTargetId, statsTargetId);
}

function verifiedGeometryForTarget(lookAtEvidence = null, fallbackTargetId = null, displayContext = {}) {
  const targetId = normalizeTelemetryObjectId(
    lookAtEvidence?.primaryObjectId || fallbackTargetId
  );
  const portal = normalizedPortal(displayContext?.portal);
  const worldxVisible = displayContext?.worldxVisible === true;
  const moggVisible = displayContext?.moggVisible === true || displayContext?.workDetailVisible === true;
  const neurodesignVisible = displayContext?.neurodesignVisible === true;

  if (portal === "maxx" && ["maxx_neurons", "maxx_receptors", "maxx_neurodesign"].includes(targetId)) {
    return neurodesignVisible
      ? VERIFIED_GEOMETRY_TOTALS.maxx_neurons_paused
      : VERIFIED_GEOMETRY_TOTALS.maxx_neurons;
  }

  if (portal === "meet-joz") {
    if (targetId === "meet_joz_flex") {
      return worldxVisible
        ? VERIFIED_GEOMETRY_TOTALS.meet_joz_flex_worldx
        : VERIFIED_GEOMETRY_TOTALS.meet_joz_flex;
    }
    if (["meet_joz_ascend", "meet_joz_ascend_particles", "meet_joz_semantic_city"].includes(targetId)) {
      return worldxVisible
        ? VERIFIED_GEOMETRY_TOTALS.meet_joz_ascend_worldx
        : VERIFIED_GEOMETRY_TOTALS.meet_joz_ascend;
    }
    if (targetId === "meet_joz_mogg") {
      if (moggVisible) return VERIFIED_GEOMETRY_TOTALS.meet_joz_skills;
      if (worldxVisible) return VERIFIED_GEOMETRY_TOTALS.meet_joz_mogg_worldx;
      return VERIFIED_GEOMETRY_TOTALS.meet_joz_mogg;
    }
  }

  if (
    portal === "root"
    && [
      "root_ascend",
      "root_aura",
      "root_sector_left",
      "root_sector_right",
      "root_sector_lower",
    ].includes(targetId)
  ) {
    return VERIFIED_GEOMETRY_TOTALS.root_ascend_aura;
  }

  return VERIFIED_GEOMETRY_TOTALS[targetId] || null;
}

export function buildSensorMetricLines(
  camera = {},
  lookAtEvidence = null,
  viewStats = null,
  rendererStats = null,
  fallbackTargetId = null,
  displayContext = {}
) {
  const orientationMetrics = [];
  const sceneMetrics = [];
  const performanceMetrics = [];
  const formatDegrees = (value) => {
    const number = Number(value);
    if (!Number.isFinite(number)) return null;
    return `${number >= 0 ? "+" : ""}${number.toFixed(1)}`;
  };

  const yaw = formatDegrees(camera.yawDegrees);
  const pitch = formatDegrees(camera.pitchDegrees);
  if (pitch) orientationMetrics.push(`Elevation: ${pitch}`);
  if (yaw) orientationMetrics.push(`Azimuth: ${yaw}`);
  const primaryId = String(lookAtEvidence?.primaryObjectId || "").toLowerCase();
  sceneMetrics.push(`Track: ${primaryId ? trackWord(primaryId) : "Scanning"}`);

  const formatCount = (value) => new Intl.NumberFormat("en-US", { maximumFractionDigits: 0 }).format(Number(value) || 0);
  const liveGeometryCurrent = geometryStatsMatchLookAt(viewStats, lookAtEvidence);
  const hasDisplayContext = Object.keys(displayContext || {}).length > 0;
  const verifiedGeometry = liveGeometryCurrent && !hasDisplayContext
    ? null
    : verifiedGeometryForTarget(lookAtEvidence, fallbackTargetId, displayContext);
  const geometryStats = verifiedGeometry || (liveGeometryCurrent ? viewStats : null);
  if (geometryStats && Number(geometryStats.vertices) > 0) {
    performanceMetrics.push(`Vertices: ${formatCount(geometryStats.vertices)}`);
  }
  if (geometryStats && Number(geometryStats.faces) > 0) {
    performanceMetrics.push(`Faces: ${formatCount(geometryStats.faces)}`);
  }
  if (Number.isFinite(Number(rendererStats?.fps)) && Number.isFinite(Number(rendererStats?.ms))) {
    performanceMetrics.push(`FPS: ${Math.round(Number(rendererStats.fps))}`, `Frames: ${Number(rendererStats.ms).toFixed(1)}ms`);
  }

  return [
    orientationMetrics.join(SPATIAL_AI_SEPARATOR) || "Camera scanning",
    [...sceneMetrics, ...performanceMetrics.slice(0, 1)].filter(Boolean).join(SPATIAL_AI_SEPARATOR),
    performanceMetrics.slice(1).join(SPATIAL_AI_SEPARATOR)
  ];
}

export function buildSensorMetricSentence(camera = {}, lookAtEvidence = null, viewStats = null, rendererStats = null, fallbackTargetId = null, displayContext = {}) {
  return `Spatial AI: ${buildSensorMetricLines(camera, lookAtEvidence, viewStats, rendererStats, fallbackTargetId, displayContext)
    .filter(Boolean)
    .join("\n")}`;
}

function meetJozMetricTarget(displayRequest = {}) {
  if (normalizedPortal(displayRequest?.experience?.portal) !== "meet-joz") return null;
  const state = `${displayRequest?.experience?.stage || ""} ${displayRequest?.experience?.focusedEntityId || ""}`.toLowerCase();
  if (/detail|panel/.test(state)) return "meet_joz_skills_panel";
  if (/mogg|skills|workf/.test(state)) return "meet_joz_mogg";
  if (/ascend|discover|worldx/.test(state)) return "meet_joz_ascend";
  return "meet_joz_flex";
}

function formatSensorMetrics(camera = {}, lookAtEvidence = null, viewStats = null, rendererStats = null) {
  return buildSensorMetricLines(camera, lookAtEvidence, viewStats, rendererStats)
    .filter(Boolean)
    .join(SPATIAL_AI_SEPARATOR);
}

const SEMANTIC_FOCUS_LABELS = {
  root_aura: "Aura",
  root_ascend: "Ascend",
  root_brain: "Exocortex",
  root_enter: "Enter",
  root_gold_pill: "Gold Pill",
  root_sector_left: "Left Sector",
  root_sector_right: "Right Sector",
  root_sector_lower: "Lower Sector",
  root_fluid_co_creation: "Human–AI co-creation field",
  meet_joz_flex: "Flex",
  meet_joz_ascend: "Ascend",
  meet_joz_ascend_particles: "Ascend particles",
  meet_joz_mogg: "Mogg",
  meet_joz_skills: "Mogg skills",
  meet_joz_skills_panel: "Mogg work detail",
  meet_joz_semantic_city: "Worldx",
  meet_joz_heart: "Aura heart",
  meet_joz_capsule: "Gold Pill",
  maxx_neurons: "Primary neuron sequence",
  maxx_receptors: "Receptor and signal-transfer visualization",
  maxx_neurodesign: "Neurodesign",
};

const TRACK_LABELS = {
  root_enter: "Exocortex",
  root_brain: "Exocortex",
  root_gold_pill: "Gold Pill",
  root_aura: "Aura",
  root_ascend: "Ascend",
};

const TRACK_WORDS = {
  root_enter: "Exocortex",
  root_brain: "Exocortex",
  root_gold_pill: "Gold",
  root_aura: "Aura",
  root_ascend: "Ascend",
  maxx_neurons: "Neurons",
  maxx_receptors: "Receptors",
  maxx_neurodesign: "Neurodesign",
  meet_joz_flex: "Flex",
  meet_joz_ascend: "Ascend",
  meet_joz_ascend_particles: "Particles",
  meet_joz_mogg: "Mogg",
  meet_joz_skills: "Skills",
  meet_joz_skills_panel: "Skills",
  meet_joz_semantic_city: "Worldx",
  meet_joz_heart: "Heart",
  meet_joz_capsule: "Pill",
};

function semanticFocusLabel(object = {}) {
  const id = String(object.id || "").toLowerCase();
  return SEMANTIC_FOCUS_LABELS[id] || conciseLabel(humanize(object.role || id || "the current scene"));
}

function trackLabel(objectId) {
  const id = String(objectId || "").toLowerCase();
  return TRACK_LABELS[id] || semanticFocusLabel({ id });
}

function trackWord(objectId) {
  const id = String(objectId || "").toLowerCase();
  if (TRACK_WORDS[id]) return TRACK_WORDS[id];
  return trackLabel(id).split(/\s+/)[0] || "Scanning";
}

function visibleFocusLabels(objects = []) {
  return objects
    .filter((object) => object?.spatial?.inFront === true)
    .sort((left, right) => (left.spatial?.distance ?? Infinity) - (right.spatial?.distance ?? Infinity))
    .map(semanticFocusLabel)
    .filter(Boolean)
    .filter((label, index, labels) => labels.indexOf(label) === index)
    .slice(0, 3);
}

function frontObjects(request = {}) {
  const objects = Array.isArray(request.visibleSemanticObjects)
    ? request.visibleSemanticObjects
    : [];
  return objects
    .filter((object) => object?.spatial?.inFront === true)
    .sort((left, right) => (left.spatial?.distance ?? Infinity) - (right.spatial?.distance ?? Infinity));
}

function frontObjectIds(request = {}) {
  return frontObjects(request)
    .map((object) => String(object.id || "").toLowerCase())
    .filter(Boolean);
}

function centerRayTargetId(request = {}) {
  const evidence = request.lookAtEvidence;
  if (!["center-ray", "portal-spatial-model"].includes(evidence?.method)) return null;
  return String(evidence.primaryObjectId || "").trim().toLowerCase() || null;
}

function centerRayTarget(request = {}) {
  const targetId = centerRayTargetId(request);
  if (!targetId) return null;
  const observed = (request.visibleSemanticObjects || []).find(
    (object) => String(object?.id || "").trim().toLowerCase() === targetId
  );
  return observed || { id: targetId, spatial: {} };
}

function rootLookAtSentence(request = {}) {
  if (request.experience?.portal !== "root") return null;
  if (!["center-ray", "portal-spatial-model"].includes(request.lookAtEvidence?.method)) return null;
  const targetId = centerRayTargetId(request);
  if (!targetId) return ROOT_LOOK_AT_SENTENCES.moon_only;
  const candidateIds = new Set(request.lookAtEvidence?.candidateObjectIds || []);
  const targetObject = (request.visibleSemanticObjects || []).find(
    (object) => String(object?.id || "").toLowerCase() === targetId
  );
  const modelCandidate = (request.lookAtEvidence?.candidates || []).find(
    (candidate) => String(candidate?.id || "").toLowerCase() === targetId
  );
  const targetSpatial = Number.isFinite(Number(modelCandidate?.bearingDegrees)) &&
    Number.isFinite(Number(modelCandidate?.elevationDegrees))
    ? modelCandidate
    : targetObject?.spatial || {};
  const bearing = Number(targetSpatial.bearingDegrees);
  const elevation = Number(targetSpatial.elevationDegrees);
  const angularScore = Number.isFinite(bearing) && Number.isFinite(elevation)
    ? Math.hypot(bearing, elevation)
    : null;
  const preciselyCentered = Number.isFinite(angularScore)
    ? angularScore <= 15
    : request.lookAtEvidence?.confidence !== "medium";
  const relativeDirection = Number.isFinite(elevation) && Math.abs(elevation) > 15
    ? elevation > 0 ? "above" : "below"
    : Number.isFinite(bearing) && Math.abs(bearing) > 15
      ? bearing > 0 ? "to the right of" : "to the left of"
      : "toward";
  const relativePosition = relativeDirection === "toward" ? "around" : relativeDirection;
  const contextual = (label) => preciselyCentered
    ? null
    : `On the Moon, ${relativePosition} the ${label}`;
  if (targetId === "root_brain" || targetId === "root_enter") {
    return contextual("Exocortex") || ROOT_LOOK_AT_SENTENCES.exocortex;
  }
  if (
    (targetId === "root_aura" || targetId === "root_ascend") &&
    candidateIds.has("root_aura") &&
    candidateIds.has("root_ascend")
  ) {
    return contextual("Aura Ascend") || ROOT_LOOK_AT_SENTENCES.aura_ascend;
  }
  if (targetId === "root_aura") return contextual("Aura") || ROOT_LOOK_AT_SENTENCES.aura;
  if (targetId === "root_ascend") return contextual("Ascend") || ROOT_LOOK_AT_SENTENCES.ascend;
  if (targetId === "root_gold_pill") return contextual("Gold Pill") || ROOT_LOOK_AT_SENTENCES.gold_pill;
  if (targetId === "root_sector_left") return ROOT_LOOK_AT_SENTENCES.sector_left;
  if (targetId === "root_sector_right") return ROOT_LOOK_AT_SENTENCES.sector_right;
  if (targetId === "root_sector_lower") return ROOT_LOOK_AT_SENTENCES.sector_lower;
  return ROOT_LOOK_AT_SENTENCES.moon_only;
}

export function getRootPortalControls(request = {}) {
  if (normalizedPortal(request.experience?.portal) !== "root") return null;
  if (rootLookAtSentence(request) !== ROOT_LOOK_AT_SENTENCES.exocortex) return null;
  return {
    enterExocortex: {
      action: APP_ACTIONS.NAVIGATE,
      label: "enter the Exocortex",
      payload: { targetPath: "/neo/maxx" },
    },
    meetJoz: {
      action: APP_ACTIONS.OPEN_BALL_PORTAL,
      label: "Meet Joz.",
      payload: {},
    },
  };
}

function focusWithoutRootPortalControls(focus, controls) {
  const text = String(focus || "").trim();
  if (!controls || !text.endsWith(ROOT_PORTAL_ACTION_SUFFIX)) return text;
  return text.slice(0, -ROOT_PORTAL_ACTION_SUFFIX.length).trim();
}

export function getRootContactControl(request = {}) {
  if (normalizedPortal(request.experience?.portal) !== "root") return null;
  if (rootLookAtSentence(request) !== ROOT_LOOK_AT_SENTENCES.aura_ascend) return null;
  return {
    label: "link up with Joz.",
    href: "mailto:joz@meetjoz.com",
  };
}

function focusWithoutRootContactControl(focus, control) {
  const text = String(focus || "").trim();
  if (!control || !text.endsWith(ROOT_CONTACT_ACTION_SUFFIX)) return text;
  return text.slice(0, -ROOT_CONTACT_ACTION_SUFFIX.length).trim();
}

function maxxLookAtSentence(request = {}, playbackStatus = null) {
  if (request.experience?.portal !== "maxx") return null;
  if (playbackStatus?.paused === true) {
    return playbackStatus.neurodesignVisible
      ? MAXX_LOOK_AT_SENTENCES.neurodesign
      : MAXX_LOOK_AT_SENTENCES.paused;
  }
  if (playbackStatus?.paused === false) return MAXX_LOOK_AT_SENTENCES.neural_activity;
  const targetId = centerRayTargetId(request);
  const visibleIds = new Set(frontObjectIds(request));
  const state = String(
    request.experience?.focusedEntityId || request.experience?.stage || ""
  ).toLowerCase();
  if (targetId === "maxx_neurodesign" || /pause|neurodesign|elite|inside_the_brain/.test(state)) {
    return MAXX_LOOK_AT_SENTENCES.neurodesign;
  }
  if (
    targetId === "maxx_neurons" ||
    targetId === "maxx_receptors" ||
    visibleIds.has("maxx_neurons") ||
    visibleIds.has("maxx_receptors") ||
    state.includes("maxx_neurons") ||
    state.includes("maxx_receptors")
  ) {
    return MAXX_LOOK_AT_SENTENCES.neural_activity;
  }
  return MAXX_LOOK_AT_SENTENCES.inside;
}

function meetJozStateKey(request = {}) {
  const state = `${request.experience?.focusedEntityId || ""} ${request.experience?.stage || ""} ${request.experience?.layer || ""}`
    .toLowerCase()
    .replace(/[_-]+/g, " ");
  if (request.experience?.workDetailVisible === true) return "skills";
  if (/\bascend\b|\bdiscover\b/.test(state)) return "ascend";
  if (/\bflex\b|\bvibe\b/.test(state)) return "flex";
  if (request.experience?.moggVisible === true || /\bmogg\b|\bskills\b/.test(state)) {
    return "mogg";
  }
  return null;
}

function meetJozLookAtSentence(request = {}) {
  if (normalizedPortal(request.experience?.portal) !== "meet-joz") return null;
  const stateKey = meetJozStateKey(request);
  const location = MEET_JOZ_LOOK_AT_SENTENCES[stateKey] || null;
  if (request.experience?.worldxVisible !== true) return location;

  const sector = Number(request.camera?.relativeWorldxSector);
  const sectorFocus = Number.isInteger(sector) ? WORLDX_SECTORS[sector] : null;
  if (sectorFocus) {
    return `${location ? `${location} ` : ""}World X Sector: ${sectorFocus.label}.`;
  }
  return location ? `${location} World X Sector.` : "at World X.";
}

export function getMeetJozFlexControl(request = {}) {
  if (normalizedPortal(request.experience?.portal) !== "meet-joz") return null;
  if (meetJozStateKey(request) !== "flex") return null;
  return {
    action: APP_ACTIONS.MEET_JOZ_TRIGGER_FLEX,
    label: "begin your Ascension.",
    payload: {},
  };
}

export function getMeetJozMoggControl(request = {}) {
  if (normalizedPortal(request.experience?.portal) !== "meet-joz") return null;
  if (meetJozStateKey(request) !== "ascend") return null;
  return {
    action: APP_ACTIONS.MEET_JOZ_SKILLS,
    label: "next on Mogg.",
    payload: {},
  };
}

export function getMeetJozSkillsControl(request = {}) {
  if (normalizedPortal(request.experience?.portal) !== "meet-joz") return null;
  if (meetJozStateKey(request) !== "mogg") return null;
  return {
    action: APP_ACTIONS.MEET_JOZ_SKILLS,
    label: "Show Skills.",
    payload: {},
  };
}

export function getMeetJozSkillsInWorldControl(request = {}) {
  if (normalizedPortal(request.experience?.portal) !== "meet-joz") return null;
  if (meetJozStateKey(request) !== "skills") return null;
  return {
    label: "View in Your World.",
    prompt: "show skills in your world",
    options: { worldModelDemo: true },
  };
}

export function getMaxxViewInWorldControl(request = {}) {
  if (normalizedPortal(request.experience?.portal) !== "maxx") return null;
  return {
    label: "View in Your World.",
    prompt: "show neurons in your world",
    options: { worldModelDemo: true },
  };
}

export function getWorldxSectorControl(request = {}) {
  if (normalizedPortal(request.experience?.portal) !== "meet-joz") return null;
  if (request.experience?.worldxVisible !== true) return null;

  const sectorIndex = Number(request.camera?.relativeWorldxSector);
  const sector = Number.isInteger(sectorIndex) ? WORLDX_SECTORS[sectorIndex] : null;
  if (!sector) return null;

  return {
    label: `${sector.label}.`,
    sectorIndex,
    sectorLabel: sector.label,
    prompt: `What does World X Sector: ${sector.label} mean?`,
  };
}

function getMeetJozProgressControl(request = {}) {
  return getMeetJozSkillsInWorldControl(request)
    || getMeetJozSkillsControl(request)
    || getMeetJozMoggControl(request)
    || getMeetJozFlexControl(request);
}

function splitFocusControl(focus, control) {
  const text = String(focus || "").trim();
  if (!control?.label) return { before: text, after: "" };
  const index = text.indexOf(control.label);
  if (index < 0) return { before: text, after: "" };
  return {
    before: text.slice(0, index).trimEnd(),
    after: text.slice(index + control.label.length).trimStart(),
  };
}

export function getMaxxPlaybackControl(request = {}, playbackStatus = null) {
  if (normalizedPortal(request.experience?.portal) !== "maxx") return null;
  const sentence = maxxLookAtSentence(request, playbackStatus);
  if (
    sentence === MAXX_LOOK_AT_SENTENCES.neurodesign ||
    sentence === MAXX_LOOK_AT_SENTENCES.paused
  ) {
    return { action: "resume", label: "Resume Neural Flow." };
  }
  if (sentence === MAXX_LOOK_AT_SENTENCES.neural_activity) {
    return { action: "pause", label: "Pause Neural Flow." };
  }
  return null;
}

function focusWithoutPlaybackControl(focus, control) {
  const text = String(focus || "").trim();
  if (!control?.label || !text.endsWith(control.label)) return text;
  return text.slice(0, -control.label.length).trim();
}

function focusWithoutViewInWorldControl(focus, control) {
  const text = String(focus || "").trim();
  if (!control?.label || !text.endsWith(control.label)) return text;
  return text.slice(0, -control.label.length).trim();
}

function focusWithoutMaxxControls(focus, playbackControl, viewInWorldControl) {
  const text = String(focus || "").trim();
  if (playbackControl?.label && viewInWorldControl?.label) {
    const playbackLabel = playbackControl.label.replace(/\.$/, "");
    const combinedSuffix = `${playbackLabel} or ${viewInWorldControl.label}`;
    if (text.endsWith(combinedSuffix)) {
      return text.slice(0, -combinedSuffix.length).trim();
    }
  }
  return focusWithoutPlaybackControl(
    focusWithoutViewInWorldControl(text, viewInWorldControl),
    playbackControl
  );
}

function aiFocusMatchesCurrentLookAt(perception, request = {}) {
  const expectedFocusObjectId = centerRayTargetId(request);
  if (perception && typeof perception === "object" && perception.focusObjectId) {
    return String(perception.focusObjectId || "").trim().toLowerCase() === String(expectedFocusObjectId || "").trim().toLowerCase();
  }
  const focus = typeof perception === "string" ? perception : perception?.lookingAt;
  const expected = rootLookAtSentence(request) || maxxLookAtSentence(request) || meetJozLookAtSentence(request);
  if (!expected) return true;
  return String(focus || "").trim().replace(/[.!]+$/, "").toLowerCase() === expected.toLowerCase();
}

function preciseSpatialDetails(object = {}) {
  const spatial = object.spatial || {};
  const details = [];
  const format = (value) => Number.isFinite(Number(value))
    ? `${Number(value).toFixed(1)}°`
    : null;
  const bearing = format(spatial.bearingDegrees);
  const elevation = format(spatial.elevationDegrees);
  const distance = Number.isFinite(Number(spatial.distance))
    ? Number(spatial.distance).toFixed(2)
    : null;
  if (bearing) details.push(`bearing ${bearing}`);
  if (elevation) details.push(`elevation ${elevation}`);
  if (distance) details.push(`distance ${distance}`);
  return details.length ? ` (${details.join(" · ")})` : "";
}

export function buildExactFocus(request = {}, maxxPlaybackStatus = null) {
  const experience = request.experience || {};
  const objects = Array.isArray(request.visibleSemanticObjects)
    ? request.visibleSemanticObjects
    : [];
  const visibleLabels = visibleFocusLabels(objects);
  const portal = experience.portal || "root";

  if (portal === "root") {
    const exactRootFocus = rootLookAtSentence(request);
    if (exactRootFocus) return exactRootFocus;
    const front = frontObjects(request);
    const frontIds = frontObjectIds(request);
    const frontById = new Map(front.map((object) => [String(object.id || "").toLowerCase(), object]));
    const primaryId = frontIds[0];
    if (primaryId === "root_brain" || primaryId === "root_enter") {
      return ROOT_LOOK_AT_SENTENCES.exocortex;
    }
    if (primaryId === "root_ascend") {
      return `on the Moon, Monitoring Ascend${preciseSpatialDetails(frontById.get("root_ascend"))}. Contact Joz here.`;
    }
    if (primaryId === "root_aura") {
      return `on the Moon, Monitoring the Aura field${preciseSpatialDetails(frontById.get("root_aura"))}. Rotate to Ascend or the Exocortex.`;
    }
    if (primaryId === "root_gold_pill") {
      return `on the Moon, Monitoring the Gold Pill${preciseSpatialDetails(frontById.get("root_gold_pill"))}. Enter Meet Joz.`;
    }
    return "on the Moon, Monitoring the surrounding space.";
  }

  if (portal === "maxx") {
    return maxxLookAtSentence(request, maxxPlaybackStatus);
  }

  const exactMeetJozFocus = meetJozLookAtSentence(request);
  if (exactMeetJozFocus) return exactMeetJozFocus;

  if (portal === "meet-joz") {
    const stage = String(
      experience.focusedEntityId || experience.stage || experience.layer || ""
    ).toLowerCase();
    const stageLabel = stage.includes("flex") || stage === "vibe"
      ? "Flex"
      : stage.includes("ascend") || stage === "discover"
        ? "Ascend"
        : stage.includes("mogg") || stage.includes("skills") || stage === "skills"
          ? "Mogg"
          : null;
    const stageObjectId = stageLabel === "Flex"
      ? "meet_joz_flex"
      : stageLabel === "Ascend"
        ? "meet_joz_ascend"
        : stageLabel === "Mogg"
          ? "meet_joz_mogg"
          : null;
    if (stageLabel && frontObjectIds(request).includes(stageObjectId)) {
      const primary = frontObjects(request)[0];
      return [`${stageLabel}${preciseSpatialDetails(primary)}`, ...visibleLabels.filter((label) => label !== stageLabel)]
        .slice(0, 4)
        .join(" · ");
    }
  }

  return visibleLabels.join(" · ") || "Current spatial scene";
}

export function buildExactAvailableActions(request = {}) {
  const portal = request.experience?.portal || "root";
  if (portal !== "root") return null;

  const exactTargetId = centerRayTargetId(request);
  const frontIds = exactTargetId ? [exactTargetId] : frontObjectIds(request);
  const primaryId = frontIds[0];
  if (primaryId === "root_brain" || primaryId === "root_enter") {
    return "Enter the Exocortex · Rotate the view";
  }
  if (primaryId === "root_ascend") {
    return "Contact Joz through Ascend · Rotate the view";
  }
  if (primaryId === "root_aura") {
    return "Rotate through the Aura field · Rotate the view";
  }
  if (primaryId === "root_gold_pill") {
    return "Enter Meet Joz · Rotate the view";
  }
  return "Enter the Exocortex or Meet Joz · Rotate the view";
}

export function buildImmediateTelemetryPerception(request = {}) {
  const view = request.camera || {};
  const objects = Array.isArray(request.visibleSemanticObjects)
    ? request.visibleSemanticObjects
    : [];
  const horizontal = humanize(view.horizontalSector || "forward").toLowerCase();
  const vertical = humanize(view.verticalBand || "level").toLowerCase();
  const yaw = Number.isFinite(view.yawDegrees) ? ` · ${Math.round(view.yawDegrees)}°` : "";
  const pitch = Number.isFinite(view.pitchDegrees) ? ` · ${Math.round(view.pitchDegrees)}° pitch` : "";
  const actions = Array.isArray(request.availableActions)
    ? request.availableActions
      .filter((action) => String(action.id || action.action || "").toLowerCase() !== "ask_joz_maxx")
      .map((action) => action.label || action.action || action.id)
      .filter(Boolean)
      .slice(0, 5)
      .map(humanize)
      .join(", ")
    : "";
  const exactActions = buildExactAvailableActions(request);

  return {
    orientation: `${horizontal} at ${vertical}${yaw}${pitch}`,
    lookingAt: buildExactFocus(request),
    canDo: exactActions || actions || "Rotate the view or ask Joz MAXX.",
    confidence: "telemetry",
    source: "telemetry",
  };
}

export function cameraTelemetryDisplayRequest(detail = {}) {
  const view = detail.cameraState?.view || {};
  const evidence = detail.lookAtEvidence || {};
  const candidates = Array.isArray(evidence.candidates)
    ? evidence.candidates.slice(0, 8).map((candidate) => ({
        id: clean(candidate?.id, 96),
        asset: clean(candidate?.asset, 120) || null,
        bearingDegrees: finiteNumber(candidate?.bearingDegrees),
        elevationDegrees: finiteNumber(candidate?.elevationDegrees),
        angularDistanceDegrees: finiteNumber(candidate?.angularDistanceDegrees),
      })).filter((candidate) => candidate.id)
    : [];
  return {
    experience: {
      portal: normalizedPortal(detail.portal),
      focusedEntityId: clean(detail.mesh, 96) || null,
      stage: clean(detail.stage, 96) || null,
      worldxVisible: detail.worldxVisible === true,
    },
    camera: {
      yawDegrees: finiteNumber(view.yaw_degrees),
      headingDegrees: Number.isFinite(Number(view.yaw_degrees))
        ? ((Number(view.yaw_degrees) % 360) + 360) % 360
        : null,
      pitchDegrees: finiteNumber(view.pitch_degrees),
      yawVelocity: finiteNumber(view.yaw_velocity),
      pitchVelocity: finiteNumber(view.pitch_velocity),
      stability: finiteNumber(view.stability),
      horizontalSector: clean(view.horizontal_sector, 48) || null,
      verticalBand: clean(view.vertical_band, 48) || null,
      relativeWorldxSector: Number.isInteger(Number(detail.worldxSector))
        && Number(detail.worldxSector) >= 0
        && Number(detail.worldxSector) < WORLDX_SECTORS.length
        ? Number(detail.worldxSector)
        : detail.worldxVisible === true
          ? worldxSectorIndex(Number(view.yaw_degrees), Number(view.pitch_degrees))
          : null,
      centerEvidence: detail.centerEvidence && typeof detail.centerEvidence === "object"
        ? {
            method: clean(detail.centerEvidence.method, 48) || null,
            headingDegrees: finiteNumber(detail.centerEvidence.headingDegrees),
            pitchDegrees: finiteNumber(detail.centerEvidence.pitchDegrees),
            sectorIndex: finiteNumber(detail.centerEvidence.sectorIndex),
            label: clean(detail.centerEvidence.label, 120) || null,
            calibratedHeadingDegrees: finiteNumber(detail.centerEvidence.calibratedHeadingDegrees),
            calibratedElevationDegrees: finiteNumber(detail.centerEvidence.calibratedElevationDegrees),
            viewportCenterDistance: finiteNumber(detail.centerEvidence.viewportCenterDistance),
            modelAsset: clean(detail.centerEvidence.modelAsset, 120) || null,
            canvasCoordinates: detail.centerEvidence.canvasCoordinates && typeof detail.centerEvidence.canvasCoordinates === "object"
              ? {
                  x: finiteNumber(detail.centerEvidence.canvasCoordinates.x),
                  y: finiteNumber(detail.centerEvidence.canvasCoordinates.y),
                }
              : null,
          }
        : null,
    },
    lookAtEvidence: {
      method: clean(evidence.method, 32) || null,
      primaryObjectId: clean(evidence.primaryObjectId, 96) || null,
      candidateObjectIds: candidates.map((candidate) => candidate.id),
      candidates,
      confidence: clean(evidence.confidence, 24) || "none",
    },
    visibleSemanticObjects: candidates.map((candidate) => ({
      id: candidate.id,
      spatial: {
        bearingDegrees: candidate.bearingDegrees,
        elevationDegrees: candidate.elevationDegrees,
        inFront: true,
      },
    })),
  };
}

export function selectLivePerceptionDisplayRequest(snapshotRequest = {}, telemetryRequest = null) {
  const snapshotPortal = normalizedPortal(snapshotRequest?.experience?.portal);
  const telemetryPortal = normalizedPortal(telemetryRequest?.experience?.portal);
  const telemetrySector = Number(telemetryRequest?.camera?.relativeWorldxSector);
  const snapshotStage = clean(snapshotRequest?.experience?.stage, 96);
  const telemetryStage = clean(telemetryRequest?.experience?.stage, 96);
  const stageMatches = !snapshotStage || !telemetryStage || snapshotStage === telemetryStage;
  const telemetryIsCurrentWorldx =
    snapshotPortal === "meet-joz" &&
    telemetryPortal === snapshotPortal &&
    snapshotRequest?.experience?.worldxVisible === true &&
    telemetryRequest?.experience?.worldxVisible === true &&
    stageMatches &&
    Number.isInteger(telemetrySector) &&
    telemetrySector >= 0 &&
    telemetrySector < WORLDX_SECTORS.length;

  return telemetryIsCurrentWorldx ? telemetryRequest : snapshotRequest;
}

function rememberPerception(cache, signature, perception) {
  cache.set(signature, perception);
  if (cache.size <= PERCEPTION_CACHE_LIMIT) return;
  cache.delete(cache.keys().next().value);
}

export function LivePerception(props) {
  const [sceneSnapshot, setSceneSnapshot] = useState(() => (
    typeof window !== "undefined" ? window.__sceneObservationSnapshot || null : null
  ));
  const [cameraOnlySnapshot, setCameraOnlySnapshot] = useState(null);
  const [worldxTelemetryRequest, setWorldxTelemetryRequest] = useState(null);
  const [scenePerformanceVersion, setScenePerformanceVersion] = useState(0);
  const [perceptionState, setPerceptionState] = useState({ portal: null, perception: null, status: "understanding" });
  const [isCollapsed, setIsCollapsed] = useState(false);
  const [isCollapsing, setIsCollapsing] = useState(false);
  const [isExpanding, setIsExpanding] = useState(false);
  const [collapsedCubeBottom, setCollapsedCubeBottom] = useState(null);
  const [isShortViewport, setIsShortViewport] = useState(() => (
    typeof window !== "undefined" && window.matchMedia?.("(max-height: 300px)").matches === true
  ));
  const [maxxPlaybackStatus, setMaxxPlaybackStatus] = useState(() => (
    typeof window !== "undefined"
      ? readMaxxPlaybackStatus(window.__maxxAnimationState)
      : null
  ));
  const maxxPlaybackStatusRef = useRef(maxxPlaybackStatus);
  maxxPlaybackStatusRef.current = maxxPlaybackStatus;
  const cacheRef = useRef(new Map());
  const pendingRef = useRef(null);
  const inFlightRef = useRef(false);
  const mountedRef = useRef(true);
  const latestSignatureRef = useRef("");
  const perceptionTimerRef = useRef(null);
  const collapseTimerRef = useRef(null);
  const lastRequestStartedAtRef = useRef(0);
  const cubePoseRef = useRef({ x: 0.48, y: -0.62, z: 0 });
  const expandedCubeRef = useRef(null);
  const focusSentenceRef = useRef(null);
  const focusTextRef = useRef(null);
  const lastFocusSentenceRef = useRef("");
  const lastFocusWordsRef = useRef([]);
  const sensorMetricTextRef = useRef(null);
  const latestSensorRequestRef = useRef(null);
  const rootPortalControlsRef = useRef(null);
  const rootContactControlRef = useRef(null);
  const meetJozProgressControlRef = useRef(null);
  const meetJozProgressButtonRef = useRef(null);
  const focusBetweenRef = useRef(null);
  const worldxSectorControlRef = useRef(null);
  const worldxSectorButtonRef = useRef(null);
  const latestWorldxSectorControlRef = useRef(null);

  useEffect(() => {
    if (collapseTimerRef.current) {
      clearTimeout(collapseTimerRef.current);
      collapseTimerRef.current = null;
    }
    setIsCollapsed(false);
    setIsCollapsing(false);
    setIsExpanding(false);
    setCollapsedCubeBottom(null);
    cubePoseRef.current = { x: 0.48, y: -0.62, z: 0 };
  }, [props.currentPortal]);

  useEffect(() => {
    if (typeof window === "undefined" || !window.matchMedia) return undefined;
    const shortViewportQuery = window.matchMedia("(max-height: 300px)");
    const syncShortViewport = () => setIsShortViewport(shortViewportQuery.matches);
    syncShortViewport();
    if (shortViewportQuery.addEventListener) {
      shortViewportQuery.addEventListener("change", syncShortViewport);
    } else {
      shortViewportQuery.addListener?.(syncShortViewport);
    }
    return () => {
      if (shortViewportQuery.removeEventListener) {
        shortViewportQuery.removeEventListener("change", syncShortViewport);
      } else {
        shortViewportQuery.removeListener?.(syncShortViewport);
      }
    };
  }, []);

  useEffect(() => {
    if (typeof window === "undefined" || normalizedPortal(props.currentPortal) !== "maxx") {
      setMaxxPlaybackStatus(null);
      return undefined;
    }

    const syncPlaybackStatus = (event) => {
      const next = readMaxxPlaybackStatus(event?.detail ?? window.__maxxAnimationState);
      setMaxxPlaybackStatus((current) => (
        current?.paused === next?.paused &&
        current?.neurodesignVisible === next?.neurodesignVisible
          ? current
          : next
      ));
    };

    syncPlaybackStatus();
    window.addEventListener(MAXX_PLAYBACK_STATE_EVENT, syncPlaybackStatus);
    return () => window.removeEventListener(MAXX_PLAYBACK_STATE_EVENT, syncPlaybackStatus);
  }, [props.currentPortal]);
  const focusSuffixRef = useRef(null);
  const cameraViewStatsRef = useRef(
    typeof window !== "undefined"
      ? window.__scenePerformanceStats?.cameraViewStats || null
      : null
  );
  const rendererStatsRef = useRef(
    typeof window !== "undefined"
      ? window.__scenePerformanceStats?.rendererStats || null
      : null
  );

  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
      if (collapseTimerRef.current) clearTimeout(collapseTimerRef.current);
    };
  }, []);

  useEffect(() => {
    if (typeof window === "undefined") return undefined;
    const handleSceneObservation = (event) => {
      const detail = event.detail || null;
      if (detail?.cameraOnly) {
        // CameraControls owns the interaction loop. HUD reconciliation is
        // deliberately lower priority so it cannot interrupt pointer easing.
        startTransition(() => setCameraOnlySnapshot(detail));
        return;
      }
      setCameraOnlySnapshot(null);
      startTransition(() => setSceneSnapshot(detail));
    };
    setSceneSnapshot(window.__sceneObservationSnapshot || null);
    window.addEventListener("world-scene-observed", handleSceneObservation);
    return () => window.removeEventListener("world-scene-observed", handleSceneObservation);
  }, [props.currentPortal]);

  useEffect(() => {
    if (typeof window === "undefined") return undefined;
    const handleCameraTelemetry = (event) => {
      const telemetryRequest = cameraTelemetryDisplayRequest(event.detail);
      latestSensorRequestRef.current = telemetryRequest;
      const telemetrySectorControl = getWorldxSectorControl(telemetryRequest);
      latestWorldxSectorControlRef.current = telemetrySectorControl;
      startTransition(() => {
        setWorldxTelemetryRequest((current) => {
          const currentSector = Number(current?.camera?.relativeWorldxSector);
          const nextSector = Number(telemetryRequest?.camera?.relativeWorldxSector);
          const currentStage = clean(current?.experience?.stage, 96);
          const nextStage = clean(telemetryRequest?.experience?.stage, 96);
          return currentSector === nextSector && currentStage === nextStage
            ? current
            : telemetryRequest;
        });
      });
    };
    window.addEventListener("world-camera-telemetry", handleCameraTelemetry);
    return () => window.removeEventListener("world-camera-telemetry", handleCameraTelemetry);
  }, []);

  useEffect(() => {
    if (typeof window === "undefined") return undefined;
    const handleScenePerformance = (event) => {
      const stats = event.detail || window.__scenePerformanceStats || {};
      if (stats.cameraViewStats) cameraViewStatsRef.current = stats.cameraViewStats;
      if (stats.rendererStats) rendererStatsRef.current = stats.rendererStats;
      startTransition(() => setScenePerformanceVersion((version) => version + 1));
    };
    handleScenePerformance({ detail: window.__scenePerformanceStats });
    window.addEventListener("world-scene-performance", handleScenePerformance);
    return () => window.removeEventListener("world-scene-performance", handleScenePerformance);
  }, []);

  useEffect(() => {
    setWorldxTelemetryRequest(null);
  }, [props.currentPortal, props.currentMesh, props.currentStage, props.showWorldx]);

  const liveSnapshot = cameraOnlySnapshot || sceneSnapshot;
  const request = useMemo(() => buildLivePerceptionRequest({ ...props, sceneSnapshot: liveSnapshot }), [
    props.currentPortal,
    props.currentMesh,
    props.currentStage,
    props.currentPhase,
    props.showWorldx,
    props.showMogg,
    props.isWorkStepInteractive,
    props.isMobile,
    liveSnapshot,
  ]);
  const signature = useMemo(() => livePerceptionSignature(request), [request]);
  const snapshotDisplayRequest = useMemo(
    () => buildLivePerceptionRequest({
      ...props,
      sceneSnapshot: liveSnapshot,
    }),
    [
      props.currentPortal,
      props.currentMesh,
      props.currentStage,
      props.currentPhase,
      props.showWorldx,
      props.showMogg,
      props.isWorkStepInteractive,
      props.isMobile,
      liveSnapshot,
    ]
  );
  const displayRequest = selectLivePerceptionDisplayRequest(
    snapshotDisplayRequest,
    worldxTelemetryRequest
  );
  const effectiveCollapsed = isCollapsed || isShortViewport;
  latestSensorRequestRef.current = displayRequest;

  useEffect(() => {
    const portal = request.experience.portal;
    latestSignatureRef.current = signature;
    const telemetryPerception = buildImmediateTelemetryPerception(request);
    const evidenceCapture = typeof window !== "undefined"
      && window.__PORTAL_EVIDENCE_CAPTURE__ === true;
    if (evidenceCapture) {
      setPerceptionState({
        portal,
        perception: telemetryPerception,
        source: "telemetry",
        status: "ready",
      });
      pendingRef.current = null;
      if (perceptionTimerRef.current) {
        window.clearTimeout(perceptionTimerRef.current);
        perceptionTimerRef.current = null;
      }
      return undefined;
    }
    if (request.interaction?.cameraActive) {
      setPerceptionState({
        portal,
        perception: telemetryPerception,
        source: "telemetry",
        status: "understanding",
      });
      pendingRef.current = null;
      if (perceptionTimerRef.current) {
        window.clearTimeout(perceptionTimerRef.current);
        perceptionTimerRef.current = null;
      }
      return undefined;
    }
    const cached = cacheRef.current.get(signature);
    if (cached) {
      setPerceptionState({
        portal,
        perception: cached,
        source: cached.source || "ai",
        status: "ready",
      });
      return;
    }

    setPerceptionState((current) => ({
      portal,
      perception: telemetryPerception || (current.portal === portal ? current.perception : null),
      source: telemetryPerception ? "telemetry" : null,
      status: "understanding",
    }));
    pendingRef.current = { request, signature, portal };

    const drain = async () => {
      if (inFlightRef.current) return;
      inFlightRef.current = true;
      lastRequestStartedAtRef.current = Date.now();
      try {
        while (mountedRef.current && pendingRef.current) {
          const job = pendingRef.current;
          pendingRef.current = null;
          try {
            const payload = await fetchJson("/api/live-perception", {
              method: "POST",
              headers: { "Content-Type": "application/json" },
              body: JSON.stringify(job.request),
              retryHttpStatuses: [404, 502, 503, 504],
            });
            const verifiedPerception = readVerifiedAiPerception(payload);
            if (!verifiedPerception) throw new Error("Scene perception response was not verified output");
            rememberPerception(cacheRef.current, job.signature, verifiedPerception);
            if (mountedRef.current && latestSignatureRef.current === job.signature) {
              setPerceptionState({
                portal: job.portal,
                perception: verifiedPerception,
                source: verifiedPerception.source,
                status: "ready",
              });
            }
          } catch (error) {
            if (mountedRef.current && latestSignatureRef.current === job.signature) {
              setPerceptionState((current) => ({
                portal: job.portal,
                perception: current.portal === job.portal ? current.perception : null,
                source: "telemetry",
                status: "unavailable",
              }));
            }
            console.warn("AI scene perception unavailable:", error?.message || error);
          }
        }
      } finally {
        inFlightRef.current = false;
        if (mountedRef.current && pendingRef.current) {
          const elapsed = Date.now() - lastRequestStartedAtRef.current;
          const delay = Math.max(PERCEPTION_SETTLE_MS, PERCEPTION_MIN_INTERVAL_MS - elapsed);
          if (perceptionTimerRef.current) window.clearTimeout(perceptionTimerRef.current);
          perceptionTimerRef.current = window.setTimeout(() => {
            perceptionTimerRef.current = null;
            drain();
          }, delay);
        }
      }
    };

    if (perceptionTimerRef.current) window.clearTimeout(perceptionTimerRef.current);
    const elapsed = Date.now() - lastRequestStartedAtRef.current;
    const delay = Math.max(PERCEPTION_SETTLE_MS, PERCEPTION_MIN_INTERVAL_MS - elapsed);
    perceptionTimerRef.current = window.setTimeout(() => {
      perceptionTimerRef.current = null;
      drain();
    }, delay);

    return () => {
      if (perceptionTimerRef.current) {
        window.clearTimeout(perceptionTimerRef.current);
        perceptionTimerRef.current = null;
      }
    };
  }, [request, signature]);

  const perception = perceptionState.portal === request.experience.portal ? perceptionState.perception : null;
  const hasPerception = Boolean(perception);

  useEffect(() => {
    const sentence = focusSentenceRef.current;
    if (!hasPerception || isCollapsed || isShortViewport || !sentence) return undefined;

    lastFocusSentenceRef.current = sentence.textContent || "";
    lastFocusWordsRef.current = Array.from(
      focusTextRef.current?.querySelectorAll(".live-perception__focus-word") || []
    ).map((word) => word.dataset.focusWord || word.textContent || "");
    const observer = new MutationObserver(() => {
      const nextSentence = sentence.textContent || "";
      if (!nextSentence || nextSentence === lastFocusSentenceRef.current) return;
      lastFocusSentenceRef.current = nextSentence;

      const nextWords = Array.from(
        focusTextRef.current?.querySelectorAll(".live-perception__focus-word") || []
      );
      const previousWords = lastFocusWordsRef.current;
      nextWords.forEach((word, index) => {
        const changed = (word.dataset.focusWord || word.textContent || "") !== previousWords[index];
        word.classList.remove("is-focus-word-changing");
        if (changed) {
          void word.offsetWidth;
          word.classList.add("is-focus-word-changing");
        }
      });
      lastFocusWordsRef.current = nextWords.map(
        (word) => word.dataset.focusWord || word.textContent || ""
      );

      sentence.classList.remove("is-text-changing");
      void sentence.offsetWidth;
      sentence.classList.add("is-text-changing");
    });

    observer.observe(sentence, {
      childList: true,
      characterData: true,
      subtree: true,
    });
    return () => {
      observer.disconnect();
      sentence.classList.remove("is-text-changing");
      lastFocusWordsRef.current = [];
    };
  }, [hasPerception, isCollapsed, isShortViewport]);

  useEffect(() => {
    if (!(perceptionState.source === "ai" || perceptionState.source === "deterministic")
      || perceptionState.status !== "ready" || !perception) return;
    recordLivePortalAwareness({
      portal: request.experience.portal,
      perception,
      evidenceIds: perception.evidenceIds,
    });
  }, [perception, perceptionState.source, perceptionState.status, request.experience.portal]);

  if (!perception) {
    return (
      <aside
        className={`live-perception live-perception--status ${props.isJozLlmOpen ? "is-chat-open" : ""}`.trim()}
        aria-label="AI scene perception status"
        aria-live="polite"
      >
        <p>{perceptionState.status === "unavailable" ? "AI unavailable" : "Understanding"}</p>
      </aside>
    );
  }

  const exactFocus = buildExactFocus(displayRequest, maxxPlaybackStatus);
  const verifiedFocus = (perceptionState.source === "ai" || perceptionState.source === "deterministic")
    && aiFocusMatchesCurrentLookAt(perception, displayRequest)
    ? perception.lookingAt
    : null;
  const focusText = displayRequest.experience.portal === "maxx" && maxxPlaybackStatus
    ? exactFocus
    : verifiedFocus || exactFocus;
  const maxxPlaybackControl = getMaxxPlaybackControl(displayRequest, maxxPlaybackStatus);
  const maxxViewInWorldControl = getMaxxViewInWorldControl(displayRequest);
  const rootPortalControls = getRootPortalControls(displayRequest);
  const rootContactControl = getRootContactControl(displayRequest);
  const meetJozProgressControl = getMeetJozProgressControl(displayRequest);
  const worldxSectorControl = getWorldxSectorControl(displayRequest);
  latestWorldxSectorControlRef.current = worldxSectorControl;
  const fullFocusStatement = focusWithoutRootContactControl(
    focusWithoutRootPortalControls(
      focusWithoutMaxxControls(focusText, maxxPlaybackControl, maxxViewInWorldControl),
      rootPortalControls
    ),
    rootContactControl
  );
  const sectorParts = splitFocusControl(fullFocusStatement, worldxSectorControl);
  const focusParts = splitFocusControl(sectorParts.before, meetJozProgressControl);
  const focusContinuation = formatYouAreContinuation(focusParts.before);
  const dispatchControl = (control) => {
    if (!control) return;
    if (control.prompt && typeof props.askJozMaxx === "function") {
      void props.askJozMaxx(control.prompt, control.options || {});
      return;
    }
    const dispatch = props.dispatchAppAction || (
      typeof window !== "undefined" ? window.__dispatchAppAction : null
    );
    dispatch?.(control.action, control.payload);
  };
  const syncCollapsedCubeBottom = () => {
    if (typeof window === "undefined") return;
    const expandedCube = expandedCubeRef.current;
    if (!expandedCube) return;
    const distanceFromViewportBottom = window.innerHeight - expandedCube.getBoundingClientRect().bottom;
    if (!Number.isFinite(distanceFromViewportBottom)) return;
    setCollapsedCubeBottom(Math.max(2, Math.round(distanceFromViewportBottom)));
  };
  const toggleCollapsed = () => {
    if (isShortViewport) return;
    if (isCollapsed) {
      if (isExpanding) return;
      if (typeof window !== "undefined" && window.matchMedia?.("(prefers-reduced-motion: reduce)").matches) {
        setIsCollapsed(false);
        return;
      }
      setIsExpanding(true);
      collapseTimerRef.current = setTimeout(() => {
        setIsCollapsed(false);
        collapseTimerRef.current = setTimeout(() => {
          setIsExpanding(false);
          collapseTimerRef.current = null;
        }, PERCEPTION_ROLL_DURATION_MS);
      }, PERCEPTION_ROLL_DURATION_MS);
      return;
    }
    if (isCollapsing) return;
    if (typeof window !== "undefined" && window.matchMedia?.("(prefers-reduced-motion: reduce)").matches) {
      syncCollapsedCubeBottom();
      setIsCollapsed(true);
      return;
    }
    syncCollapsedCubeBottom();
    setIsCollapsing(true);
    collapseTimerRef.current = setTimeout(() => {
      setIsCollapsed(true);
      setIsCollapsing(false);
      collapseTimerRef.current = null;
    }, PERCEPTION_ROLL_DURATION_MS);
  };
  const stopCanvasPointerPropagation = (event) => event.stopPropagation();
  const askAboutWorldxSector = () => {
    const control = latestWorldxSectorControlRef.current;
    if (!control?.prompt || typeof props.askJozMaxx !== "function") return;
    void props.askJozMaxx(control.prompt, {
      worldxSector: {
        sectorIndex: control.sectorIndex,
        label: control.sectorLabel,
      },
    });
  };
  const sensorMetricLines = buildSensorMetricLines(
    displayRequest.camera,
    displayRequest.lookAtEvidence,
    cameraViewStatsRef.current,
    rendererStatsRef.current,
    meetJozMetricTarget(displayRequest),
    {
      ...displayRequest.experience,
      neurodesignVisible: displayRequest.experience.portal === "maxx"
        && maxxPlaybackStatus?.neurodesignVisible === true,
    }
  ).filter(Boolean);

  return (
    <>
    <aside
      className={`live-perception ${props.isMobile ? "is-mobile" : ""} ${perceptionState.status === "understanding" ? "is-updating" : ""} ${props.isJozLlmOpen ? "is-chat-open" : ""} ${isCollapsing ? "is-collapsing" : ""} ${isExpanding ? "is-expanding" : ""} ${effectiveCollapsed ? "is-collapsed" : ""}`.trim()}
      style={{
        ...(collapsedCubeBottom == null ? {} : { "--live-perception-collapsed-bottom": `${collapsedCubeBottom}px` }),
      }}
        aria-label={perceptionState.source === "ai"
          ? "AI-generated current experience perception"
          : perceptionState.source === "deterministic"
            ? "Verified deterministic current experience perception"
            : "Current experience perception from scene telemetry"}
      aria-live="polite"
      aria-atomic="true"
      data-perception-source={perceptionState.source || "ai"}
      data-portal={displayRequest.experience.portal}
    >
      {effectiveCollapsed ? (props.isMobile ? (
        <button
          type="button"
          className="live-perception__cube"
          onClick={toggleCollapsed}
          onPointerDown={stopCanvasPointerPropagation}
          onPointerMove={stopCanvasPointerPropagation}
          onPointerUp={stopCanvasPointerPropagation}
          onPointerCancel={stopCanvasPointerPropagation}
          aria-label="Expand Spatial AI Perception"
          title="Expand Spatial AI Perception"
        >
          <LivePerceptionCubeMesh
            camera={displayRequest.camera}
            poseRef={cubePoseRef}
            scale={isExpanding ? 0 : 1}
            initialScale={isExpanding ? 1 : 0}
          />
        </button>
      ) : null) : (
        <>
          {props.isMobile && (
            <button
              type="button"
              className="live-perception__mobile-cube"
              onClick={toggleCollapsed}
              onPointerDown={stopCanvasPointerPropagation}
              onPointerMove={stopCanvasPointerPropagation}
              onPointerUp={stopCanvasPointerPropagation}
              onPointerCancel={stopCanvasPointerPropagation}
              ref={expandedCubeRef}
              aria-label="Collapse Spatial AI Perception"
              title="Collapse Spatial AI Perception"
            >
              <LivePerceptionCubeMesh
                camera={displayRequest.camera}
                poseRef={cubePoseRef}
                scale={isCollapsing ? 0 : 1}
                initialScale={1}
              />
            </button>
          )}
          <div className="live-perception__expanded">
          <p className="live-perception__focus" ref={focusSentenceRef}>
            <span className="live-perception__label">You are</span>{" "}
            <span ref={focusTextRef}>
              {renderRollingFocusText(focusContinuation)}
            </span>
            {displayRequest.experience.portal === "root" && (
              <>
                <span ref={rootPortalControlsRef} hidden={!rootPortalControls}>
                  {" "}
                  <button
                    type="button"
                    className="live-perception__action"
                    onClick={(event) => {
                      stopCanvasPointerPropagation(event);
                      dispatchControl(rootPortalControls?.enterExocortex);
                    }}
                    onPointerDown={stopCanvasPointerPropagation}
                    onPointerMove={stopCanvasPointerPropagation}
                    onPointerUp={stopCanvasPointerPropagation}
                    onPointerCancel={stopCanvasPointerPropagation}
                  >
                    enter the Exocortex
                  </button>
                  {" or "}
                  <button
                    type="button"
                    className="live-perception__action"
                    onClick={() => dispatchControl(rootPortalControls?.meetJoz)}
                  >
                    Meet Joz.
                  </button>
                </span>
                <span ref={rootContactControlRef} hidden={!rootContactControl}>
                  {" "}
                  <a
                    className="live-perception__action"
                    href={rootContactControl?.href}
                  >
                    link up with Joz.
                  </a>
                </span>
              </>
            )}
            {maxxPlaybackControl && (
              <>
                {" "}
                <button
                  type="button"
                  className="live-perception__action"
                  onClick={() => requestMaxxPlayback(maxxPlaybackControl.action, { source: "live_perception" })}
                  aria-label={maxxPlaybackControl.label.replace(/\.$/, "")}
                >
                  {maxxPlaybackControl.label}
                </button>
              </>
            )}
            {maxxViewInWorldControl && (
              <>
                {maxxPlaybackControl ? " or " : " "}
                <button
                  type="button"
                  className="live-perception__action"
                  onClick={() => dispatchControl(maxxViewInWorldControl)}
                  aria-label="View neurons in your world"
                >
                  {maxxViewInWorldControl.label}
                </button>
              </>
            )}
            {displayRequest.experience.portal === "meet-joz" && (
              <>
                <span ref={meetJozProgressControlRef} hidden={!meetJozProgressControl}>
                  {" "}
                  <button
                    type="button"
                    className="live-perception__action"
                    onClick={() => dispatchControl(meetJozProgressControl)}
                    ref={meetJozProgressButtonRef}
                  >
                    {meetJozProgressControl?.label}
                  </button>
                </span>
                <span ref={focusBetweenRef}>{focusParts.after ? ` ${focusParts.after}` : ""}</span>
                <span ref={worldxSectorControlRef} hidden={!worldxSectorControl}>
                  {" "}
                  <button
                    type="button"
                    className="live-perception__action"
                    onClick={askAboutWorldxSector}
                    ref={worldxSectorButtonRef}
                    aria-label={worldxSectorControl
                      ? `Ask Joz MAXX about ${worldxSectorControl.sectorLabel}`
                      : "Ask Joz MAXX about this World X sector"}
                  >
                    {worldxSectorControl?.label}
                  </button>
                </span>
                <span ref={focusSuffixRef}>{sectorParts.after ? ` ${sectorParts.after}` : ""}</span>
              </>
            )}
          </p>
          <p className="live-perception__orientation">
            <span className="live-perception__telemetry-group">
              <span className="live-perception__orientation-copy">
                <span className="live-perception__metric-flow">
                  <span
                    className="live-perception__metric-sentence"
                    ref={sensorMetricTextRef}
                    data-performance-version={scenePerformanceVersion}
                  >
                    <span className="live-perception__metric-grid-center">
                      {sensorMetricLines.map((line, index) => (
                        <span className={`live-perception__metric-grid-line live-perception__metric-grid-line--${index + 1}`} key={`metric-line-${index}`}>
                          {index === 0 && (
                            <span className="live-perception__metric-label">Spatial AI:</span>
                          )}
                          {index === 0 && " "}
                          <span
                            className={`live-perception__metric-line live-perception__metric-line--${index + 1}`}
                          >
                            {renderMetricLine(line, index)}
                          </span>
                        </span>
                      ))}
                    </span>
                  </span>
                </span>
              </span>
            </span>
          </p>
          </div>
        </>
      )}
    </aside>
    </>
  );
}
